// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { onAuthStateChanged }        from "firebase/auth";
import { ref, onValue, update, off, get } from "firebase/database";
import { auth, db }                  from "../firebase/config";
import { isAdminUser }               from "../constants/roles";
import i18n                          from "../i18n";
import {
  login,
  register,
  logout,
  loginWithGoogle,
  checkBlockStatus,
  deleteUserAccount,
  linkGoogleToEmail,
  syncPublicProfile,
  _googleProfileWriting,
} from "../firebase/auth";

const AuthContext = createContext(null);

// ─── uid'yi konsol / JSON çıktısından gizle ───────────────────────────────────
function maskUid(firebaseUser) {
  try {
    Object.defineProperty(firebaseUser, "uid", {
      enumerable:   false,  // console.log / JSON.stringify'da görünmez
      configurable: true,   // Firebase SDK yeniden tanımlayabilsin
      writable:     false,
    });
  } catch (_) {}
  return firebaseUser;
}

// ─── /usernames benzersizlik kontrolü ─────────────────────────────────────────
// Büyük/küçük harf DUYARLI — "Dhmm" ve "dhmm" farklı isimler.
export async function isDisplayNameAvailable(displayName, ownUid = null) {
  const { ref: dbRef, get } = await import("firebase/database");
  const key = displayName.trim().replace(/[.#$[\]/]/g, "");
  if (!key) return true;
  try {
    const snap = await get(dbRef(db, `usernames/${key}`));
    if (!snap.exists()) return true;
    return snap.val()?.uid === ownUid;
  } catch {
    return true;
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [user, setUser]                     = useState(null);
  const [userData, setUserData]             = useState(null);
  const [isAdmin, setIsAdmin]               = useState(false);
  const [isBlocked, setIsBlocked]           = useState(false);
  const [blockedMessage, setBlockedMessage] = useState("");
  const [loading, setLoading]               = useState(true);
  // Google + email hesabı çakışması — bağlama gerekli
  const [linkRequired, setLinkRequired]     = useState(false);
  const [linkData, setLinkData]             = useState(null); // { email, pendingCred }

  const dbListenerRef = useRef(null);
  const publicProfileSyncRef = useRef(new Set());

  function detachDbListener() {
    if (dbListenerRef.current) {
      off(dbListenerRef.current);
      dbListenerRef.current = null;
    }
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {

      // ── Google ilk kayıt: profil yazılırken bekle ───────────────────────────
      // loginWithGoogle() profil yazımı sırasında _googleProfileWriting = true olur.
      // Bu süreçte onAuthStateChanged erken tetiklenirse yarış durumu oluşur.
      // Flag true iken gelen event'i görmezden gel — profil yazımı bitince
      // Firebase yeniden tetikler ve o zaman tam profil hazır olur.
      if (_googleProfileWriting) return;

      detachDbListener();

      // ── Oturum yok ─────────────────────────────────────────────────────────
      if (!firebaseUser) {
        publicProfileSyncRef.current.clear();
        setUser(null);
        setUserData(null);
        setIsAdmin(false);
        setIsBlocked(false);
        setBlockedMessage("");
        setLoading(false);   // ← her zaman false
        return;
      }

      // ── Ban kontrolü ───────────────────────────────────────────────────────
      try {
        await checkBlockStatus(firebaseUser);
      } catch (e) {
        if (e.code === "auth/account-blocked") {
          setIsBlocked(true);
          setBlockedMessage(e.message);
          setUser(null);
          setUserData(null);
          setIsAdmin(false);
          setLoading(false);  // ← ban durumunda da false
          return;
        }
      }

      // ── Auth state erken set — UI kullanıcıyı hemen tanır ──────────────────
      setUser(maskUid(firebaseUser));
      setIsBlocked(false);
      setBlockedMessage("");

      // ── Admin UID listesinde mi? → DB beklenmeden anında yetkilen ──────────
      // Bu sayede DB okuma gecikmesi veya hatası admin'i "Yükleniyor"'da bırakmaz.
      const isAdminAccount = isAdminUser(firebaseUser);
      if (isAdminAccount) {
        setIsAdmin(true);
        setLoading(false);  // ← admin için loading burada biter
      }

      // ── Realtime DB: canlı dinleyici ───────────────────────────────────────
      // onValue hem başarı hem hata callback'i alır.
      // Hata durumunda (kural reddi, ağ kopması vb.) loading sonsuz kalmaz.
      const userRef = ref(db, `users/${firebaseUser.uid}`);
      dbListenerRef.current = userRef;

      onValue(
        userRef,
        (snap) => {
          const profileData = snap.exists() ? snap.val() : null;

          if (profileData && !publicProfileSyncRef.current.has(firebaseUser.uid)) {
            publicProfileSyncRef.current.add(firebaseUser.uid);
            syncPublicProfile(firebaseUser, profileData).catch((error) => {
              publicProfileSyncRef.current.delete(firebaseUser.uid);
              console.warn("[BoneCast] Public profil senkronizasyonu başarısız:", error.code);
            });
          }

          // ── Gerçek zamanlı ban kontrolü ────────────────────────────────────
          // Admin paneli isBanned'ı değiştirince bu callback anında tetiklenir.
          // Admin UID'leri ban kontrolünden muaf.
          const isBannedNow =
            !isAdminUser(firebaseUser) &&
            (profileData?.isBanned === true || profileData?.status === "blocked");

          if (isBannedNow) {
            // Oturumu anında kapat — kullanıcı sayfadan atılır
            import("firebase/auth").then(({ signOut }) => {
              signOut(auth).catch(() => {});
            });
            setIsBlocked(true);
            setBlockedMessage("Bu hesap kalıcı olarak engellenmiştir.");
            setUser(null);
            setUserData(null);
            setIsAdmin(false);
            setLoading(false);
            return;
          }

          const adminFlag = isAdminUser(firebaseUser);

          // ── Profil alanları için güvenli varsayılanlar ──────────────────────
          // language, nationality, bio Firebase'de yoksa boş/varsayılan dön.
          const enrichedProfile = profileData ? {
            ...profileData,
            language:    profileData.language    ?? "en",
            nationality: profileData.nationality ?? "",
            bio:         profileData.bio         ?? "",
          } : null;

          setUserData(enrichedProfile);
          setIsAdmin(adminFlag);
          setLoading(false);  // ← normal akışta loading burada biter

          // ── Dil tercihi — Firebase'den gelen değere göre i18n'i güncelle ───
          const preferredLang = profileData?.language;
          if (preferredLang && ["tr", "en"].includes(preferredLang)) {
            i18n.changeLanguage(preferredLang);
          }
        },
        (error) => {
          // DB okuma başarısız (kural reddi, ağ hatası vb.)
          // UID admin kontrolü yukarıda zaten yapıldı, userData null kalır.
          console.warn("[BoneCast] DB okuma hatası:", error.code);
          setUserData(null);
          setLoading(false);  // ← hata durumunda da loading biter — asla takılmaz
        }
      );

      // lastSeen — sadece tam profil varsa yaz (displayName boş değilse)
      // Aksi hâlde loginWithGoogle()'ın profil yazımından önce node oluşur
      // ve snap.exists() true döner → tam profil yazılmaz.
      try {
        const profileSnap = await get(ref(db, `users/${firebaseUser.uid}`));
        if (profileSnap.exists() && profileSnap.val()?.displayName) {
          await update(ref(db, `users/${firebaseUser.uid}`), { lastSeen: Date.now() });
        }
      } catch (_) {}
    });

    return () => {
      unsub();
      detachDbListener();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Google giriş wrapper — __link_required__ yakalar ──────────────────────
  async function loginWithGoogleWrapped() {
    try {
      return await loginWithGoogle();
    } catch (e) {
      if (e.code === "__link_required__") {
        // Aynı email ile email/şifre hesabı var — bağlama gerekli
        setLinkRequired(true);
        setLinkData({ email: e.email, pendingCred: e.pendingCred });
        return null;
      }
      throw e;
    }
  }

  // ── Google hesabını mevcut email/şifre hesabına bağla ──────────────────────
  async function linkGoogleAccount(password) {
    if (!linkData) throw new Error("Bağlanacak hesap verisi yok.");
    await linkGoogleToEmail({ ...linkData, password });
    setLinkRequired(false);
    setLinkData(null);
  }

  function cancelLink() {
    setLinkRequired(false);
    setLinkData(null);
  }

  return (
    <AuthContext.Provider value={{
      user,
      userData,
      isAdmin,
      isBlocked,
      blockedMessage,
      loading,
      linkRequired,
      linkData,
      login,
      register,
      logout,
      loginWithGoogle:  loginWithGoogleWrapped,
      linkGoogleAccount,
      cancelLink,
      deleteUserAccount,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
